import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Avatar,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Add as AddIcon,
    Celebration as CelebrationIcon,
} from '@mui/icons-material';
import ApiService from '../../../services/api';
import logger from '../../../utils/logger';

const Container = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const Th = styled(TableCell)(({ theme }) => ({
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#ffffff',
    fontWeight: 600,
    padding: '16px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
}));

const ImagePreview = styled(Box)({
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
});

const SmallImage = styled('img')({
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
});

const Festivals = () => {
    const navigate = useNavigate();
    const [festivals, setFestivals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [festivalToDelete, setFestivalToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchFestivals = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await ApiService.get('/admin/festival/get-all-festivals');
            const list = Array.isArray(res?.data)
                ? res.data
                : Array.isArray(res)
                    ? res
                    : Array.isArray(res?.data?.data)
                        ? res.data.data
                        : [];

            const normalized = list.map((f) => ({
                id: f.id,
                name: f.name,
                date: f.date,
                emoji: f.emoji || '🎉',
                template: f.template,
                images: f.images || [],
                status: f.status,
                createdBy: f.createdBy?.full_name || '-',
                updatedBy: f.updatedBy?.full_name || '-',
            }));
            setFestivals(normalized);
        } catch (e) {
            logger.error('Failed to load festivals:', e?.response || e);
            setError(e?.response?.data?.message || 'Failed to load festivals');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFestivals();
    }, []);

    const handleDeleteClick = (festival) => {
        setFestivalToDelete(festival);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!festivalToDelete) return;
        setDeleting(true);
        try {
            await ApiService.delete(`/admin/festival/delete-festival/${festivalToDelete.id}`);
            setFestivals((prev) => prev.filter((f) => f.id !== festivalToDelete.id));
            setDeleteDialogOpen(false);
            setFestivalToDelete(null);
        } catch (e) {
            logger.error('Failed to delete festival:', e);
            setError('Failed to delete festival');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setFestivalToDelete(null);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const isUpcoming = (dateStr) => {
        if (!dateStr) return false;
        const festivalDate = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return festivalDate >= today;
    };

    return (
        <Container>
            <Header>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Box sx={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                        }}>
                            <CelebrationIcon sx={{ color: '#fff', fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" fontWeight={700} color="#1f2937">
                                Festivals
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage festivals for EDM posts. Add festival dates and images for weekly reports.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/admin-dashboard/add-festival')}
                    sx={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        },
                    }}
                >
                    Add Festival
                </Button>
            </Header>

            <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                {loading
                    ? 'Loading festivals...'
                    : `Total ${festivals.length} festival${festivals.length === 1 ? '' : 's'} found.`}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainerStyled component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <Th>Festival</Th>
                            <Th>Date</Th>
                            <Th>Images</Th>
                            <Th>Status</Th>
                            <Th>Created By</Th>
                            <Th align="center">Actions</Th>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        ) : festivals.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="textSecondary">
                                        No festivals found. Click "Add Festival" to create one.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            festivals.map((festival) => (
                                <TableRow key={festival.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: '#fef3c7',
                                                    color: '#1e293b',
                                                    fontSize: '20px',
                                                    width: 40,
                                                    height: 40,
                                                }}
                                            >
                                                {festival.emoji}
                                            </Avatar>
                                            <Box>
                                                <Typography fontWeight={600} color="#1e293b">
                                                    {festival.name}
                                                </Typography>
                                                {festival.template && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        Template: {festival.template}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography fontWeight={500}>
                                                {formatDate(festival.date)}
                                            </Typography>
                                            {isUpcoming(festival.date) && (
                                                <Chip
                                                    label="Upcoming"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#dcfce7',
                                                        color: '#166534',
                                                        fontSize: '11px',
                                                        height: '20px',
                                                        mt: 0.5,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <ImagePreview>
                                            {festival.images && festival.images.length > 0 ? (
                                                festival.images.slice(0, 4).map((img, idx) => (
                                                    <Tooltip key={idx} title={`Image ${idx + 1}`}>
                                                        <SmallImage src={img} alt={`${festival.name} ${idx + 1}`} />
                                                    </Tooltip>
                                                ))
                                            ) : (
                                                <Typography variant="caption" color="textSecondary">
                                                    No images
                                                </Typography>
                                            )}
                                        </ImagePreview>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={festival.status ? 'Active' : 'Inactive'}
                                            size="small"
                                            sx={{
                                                bgcolor: festival.status ? '#dcfce7' : '#fee2e2',
                                                color: festival.status ? '#166534' : '#991b1b',
                                                fontWeight: 500,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="textSecondary">
                                            {festival.createdBy}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/admin-dashboard/edit-festival/${festival.id}`)}
                                                sx={{ color: '#0b91d6' }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteClick(festival)}
                                                sx={{ color: '#ef4444' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainerStyled>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Festival</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{festivalToDelete?.name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Festivals;
